import { useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError, type Paginado } from '../api/client';
import { useAuth } from '../auth/auth-context';
import { Tag } from '../components/Tag';
import { CampoTelefone } from '../components/CampoTelefone';
import {
  Campo,
  CampoBusca,
  Carregando,
  ErroBox,
  Form,
  LinhaCampos,
  Modal,
  ModalRodape,
  Paginacao,
  Secao,
  Vazio,
} from '../components/ui';
import { limpar } from './Eventos';

interface Participante {
  id: string;
  fullName: string;
  phoneE164: string;
  email: string | null;
  company: string | null;
  jobTitle: string | null;
  city: string | null;
  status: string;
  createdAt: string;
  /** Em quantos OUTROS eventos desta organização a pessoa já esteve (decisão A7). */
  outrosEventos: number;
}

const CORES_STATUS: Record<string, string> = {
  ATIVO: 'success',
  INATIVO: 'muted',
  DUPLICADO: 'warning',
  EXCLUIDO: 'muted',
  BLOQUEADO: 'danger',
};

/**
 * O endereço antigo, `/eventos/:id/participantes`, agora leva à aba.
 *
 * Mesmo motivo do conteúdo: o link pode estar numa aba aberta ou num favorito,
 * e um 404 não conta que a tela mudou de lugar.
 */
export function RedirecionarParticipantes() {
  const { id } = useParams();
  return <Navigate to={`/eventos/${id}?aba=participantes`} replace />;
}

/**
 * A lista, como bloco de uma aba do evento.
 *
 * Era uma página com cabeçalho próprio e um "← voltar" para o evento. Virou
 * aba pelo mesmo motivo do conteúdo: participante é do evento, e mandar a
 * pessoa para outro endereço para vê-lo criava uma ida e volta que não
 * levava a lugar nenhum.
 */
export function ListaDeParticipantes() {
  const { id: eventId = '' } = useParams();
  const { pode } = useAuth();
  const cliente = useQueryClient();

  const [pagina, setPagina] = useState(1);
  const [busca, setBusca] = useState('');
  const [criando, setCriando] = useState(false);
  // O último envio do formulário: a liberação do recadastro o reenvia depois de liberar.
  const [ultimoEnvio, setUltimoEnvio] = useState<Record<string, string> | null>(null);

  const params = new URLSearchParams({ page: String(pagina), perPage: '20' });
  if (busca) params.set('search', busca);

  // O país alimenta o formulário de novo participante (máscara de telefone).
  // Quem renderiza este bloco já carregou o evento, então a consulta cai no
  // cache do TanStack em vez de virar uma segunda requisição.
  const evento = useQuery({
    queryKey: ['evento', eventId],
    queryFn: () => api<{ name: string; country: string | null }>(`/events/${eventId}`),
  });

  const consulta = useQuery({
    queryKey: ['participantes', eventId, pagina, busca],
    queryFn: () => api<Paginado<Participante>>(`/events/${eventId}/participants?${params}`),
  });

  const criar = useMutation({
    mutationFn: (dados: Record<string, string>) =>
      api(`/events/${eventId}/participants`, { method: 'POST', body: limpar(dados) }),
    onSuccess: () => {
      cliente.invalidateQueries({ queryKey: ['participantes', eventId] });
      cliente.invalidateQueries({ queryKey: ['evento', eventId] });
      setCriando(false);
    },
  });

  // Sem `participants.read_pii` a API devolve telefone e e-mail mascarados.
  // A tela avisa em vez de deixar a pessoa achar que o dado está corrompido.
  const vePii = pode('participants.read_pii');

  /**
   * O telefone é de quem pediu a exclusão dos dados (LGPD). Quem pode excluir um
   * titular também pode liberar a volta dele, e vê o pedido no lugar do erro.
   * Quem não pode vê o erro, que manda pedir a um administrador.
   */
  const titularExcluido =
    criar.error instanceof ApiError && criar.error.codigo === 'TITULAR_EXCLUIDO' && ultimoEnvio;
  const podeLiberar = pode('participants.delete') && vePii;

  const fecharNovo = () => {
    setCriando(false);
    setUltimoEnvio(null);
    criar.reset();
  };

  return (
    <Secao
      titulo="Participantes"
      descricao="Quem foi convidado para este evento. É desta lista que a jornada de WhatsApp parte."
      acao={
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {pode('participants.import') && (
            <Link className="btn btn-secondary" to={`/eventos/${eventId}/importar`}>
              <i className="ti ti-file-spreadsheet" /> Importar planilha
            </Link>
          )}
          {pode('participants.create') && (
            <button className="btn btn-primary" onClick={() => setCriando(true)}>
              <i className="ti ti-plus" /> Novo participante
            </button>
          )}
        </div>
      }
    >
      {!vePii && (
        <div className="aviso" style={{ marginBottom: 16 }}>
          <i className="ti ti-eye-off" />
          <span>
            Telefone e e-mail aparecem mascarados: seu papel não inclui{' '}
            <code>participants.read_pii</code>. A restrição é aplicada no servidor.
          </span>
        </div>
      )}

      <div className="barra-filtros">
        <CampoBusca
          valor={busca}
          aoMudar={(v) => {
            setBusca(v);
            setPagina(1);
          }}
          placeholder="Buscar por nome, e-mail ou empresa…"
        />
      </div>

      {consulta.isPending && <Carregando />}
      {consulta.isError && <ErroBox erro={consulta.error} aoTentar={() => consulta.refetch()} />}

      {consulta.data &&
        (consulta.data.data.length === 0 ? (
          <Vazio
            icone="ti-users"
            titulo={busca ? 'Nenhum participante encontrado' : 'Nenhum participante ainda'}
            descricao={
              busca
                ? 'Tente outro termo de busca.'
                : 'Importe a planilha do credenciamento ou cadastre um a um.'
            }
            acao={
              !busca &&
              pode('participants.import') && (
                <Link className="btn btn-primary" to={`/eventos/${eventId}/importar`}>
                  <i className="ti ti-file-spreadsheet" /> Importar planilha
                </Link>
              )
            }
          />
        ) : (
          <div className="card" style={{ padding: 0 }}>
            <div className="tabela-envolvente">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Nome</th>
                    <th>WhatsApp</th>
                    <th>Empresa / cargo</th>
                    <th>Cidade</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {consulta.data.data.map((p) => (
                    <tr key={p.id}>
                      <td>
                        <div className="celula-principal">
                          {/* O caminho de quem está olhando o evento e quer ver
                              o que a conversa descobriu. Sem isto, a lista
                              mostrava só o cadastro e não levava a lugar
                              nenhum. */}
                          <Link to={`/participantes/${p.id}`}>{p.fullName}</Link>
                          {/*
                            Quem já veio a outra edição chega com perfil e
                            consentimento — a conversa não recomeça. Sem esta
                            marca, a plataforma sabe disso e o operador não.
                          */}
                          {p.outrosEventos > 0 && (
                            <Tag
                              type="info"
                              title={`Já participou de ${p.outrosEventos} ${p.outrosEventos === 1 ? 'outro evento' : 'outros eventos'} desta organização`}
                            >
                              <i className="ti ti-user-check" /> já veio
                            </Tag>
                          )}
                        </div>
                        {p.email && <div className="celula-secundaria">{p.email}</div>}
                      </td>
                      <td className="mono">{p.phoneE164}</td>
                      <td>
                        {p.company || <span style={{ color: 'var(--text-dim)' }}>—</span>}
                        {p.jobTitle && <div className="celula-secundaria">{p.jobTitle}</div>}
                      </td>
                      <td>{p.city || <span style={{ color: 'var(--text-dim)' }}>—</span>}</td>
                      <td>
                        <Tag type={CORES_STATUS[p.status] ?? 'muted'}>{p.status}</Tag>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Paginacao meta={consulta.data.meta} aoMudar={setPagina} />
          </div>
        ))}

      {criando && (
        <Modal
          titulo="Novo participante"
          descricao="O telefone é normalizado para E.164 pelo servidor — pode colar em qualquer formato."
          aoFechar={fecharNovo}
          largura={560}
        >
          {titularExcluido && podeLiberar && (
            <LiberarRecadastro
              envio={titularExcluido}
              pais={evento.data?.country ?? null}
              cadastrando={criar.isPending}
              aoLiberar={() => criar.mutate(titularExcluido)}
            />
          )}

          <Form
            aoEnviar={(dados) => {
              setUltimoEnvio(dados);
              criar.mutate(dados);
            }}
          >
            {criar.isError && !(titularExcluido && podeLiberar) && (
              <div className="form-erro">{(criar.error as Error).message}</div>
            )}

            <Campo label="Nome completo" name="fullName" obrigatorio autoFocus />

            <LinhaCampos>
              {/* `key` no país do evento: sem ela, o campo montava antes de a
                  consulta responder e ficava preso no Brasil mesmo num evento
                  português. */}
              <CampoTelefone
                key={evento.data?.country ?? 'carregando'}
                paisInicial={evento.data?.country}
                obrigatorio
              />
              <Campo label="E-mail" name="email" tipo="email" opcional />
            </LinhaCampos>

            <LinhaCampos>
              <Campo label="Empresa" name="company" opcional />
              <Campo label="Cargo" name="jobTitle" opcional />
            </LinhaCampos>

            {/* A "Base legal (LGPD)" saiu em 17/10/2026: só ficava gravada, não mudava
                nada — a jornada pede a autorização na primeira etapa de qualquer
                jeito. Sem ela o campo fica vazio, como já ficava na importação. */}
            <Campo label="Cidade" name="city" opcional />

            <ModalRodape>
              <button type="button" className="btn btn-secondary" onClick={fecharNovo}>
                Cancelar
              </button>
              <button className="btn btn-primary" type="submit" disabled={criar.isPending}>
                {criar.isPending ? 'Salvando…' : 'Cadastrar'}
              </button>
            </ModalRodape>
          </Form>
        </Modal>
      )}
    </Secao>
  );
}

/**
 * Liberar o recadastro de quem pediu a exclusão dos dados (03/11/2026).
 *
 * Aparece na janela de novo participante quando o cadastro é recusado porque o
 * telefone é de um titular excluído. Liberar não traz nada de volta: o número
 * entra como uma pessoa nova. O pedido da própria pessoa fica na auditoria.
 */
function LiberarRecadastro({
  envio,
  pais,
  cadastrando,
  aoLiberar,
}: {
  envio: Record<string, string>;
  pais: string | null;
  cadastrando: boolean;
  aoLiberar: () => void;
}) {
  const [motivo, setMotivo] = useState('');

  const liberar = useMutation({
    mutationFn: () =>
      api<{ liberado: boolean }>('/people/liberar-recadastro', {
        method: 'POST',
        body: { phone: envio.phone ?? '', ...(pais ? { country: pais } : {}), reason: motivo.trim() },
      }),
    onSuccess: aoLiberar,
  });

  const ocupado = liberar.isPending || cadastrando;

  return (
    <div style={{ marginBottom: 16 }}>
      <div className="aviso aviso-info" style={{ marginBottom: 14 }}>
        <i className="ti ti-lock" />
        <span>
          Este telefone é de alguém que <strong>pediu a exclusão dos dados</strong>. Só cadastre de
          novo se a própria pessoa pediu para voltar. Nada do que foi apagado volta: ela entra como
          um cadastro novo.
        </span>
      </div>

      <Campo
        label="Como a pessoa pediu para voltar"
        name="motivoDaLiberacao"
        linhas={3}
        valor={motivo}
        aoMudar={setMotivo}
        placeholder="Ex.: pediu por WhatsApp em 03/11 para participar da próxima edição."
        ajuda="Fica na auditoria. É a prova de que a própria pessoa pediu."
      />

      {liberar.isError && <ErroBox erro={liberar.error} />}

      <button
        type="button"
        className="btn btn-primary"
        disabled={motivo.trim().length < 5 || ocupado}
        onClick={() => liberar.mutate()}
      >
        {ocupado ? 'Liberando…' : 'Liberar e cadastrar'}
      </button>
    </div>
  );
}
